import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { HighlightPipe, ModalService } from '@/app/core';
import { Component, inject } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  ConfirmationService,
  ConfirmEventType,
  MessageService,
} from 'primeng/api';

@Component({
    standalone: true,
    selector: 'app-dialog',
    templateUrl: './dialog.component.html',
    styleUrl: './dialog.component.css',
    imports: [CommonModule, ToastModule, ConfirmDialogModule, HighlightPipe, ButtonComponent],
    providers: [ConfirmationService, MessageService],
})
export class DialogComponent {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  message = '';
  header = '';
  icon = '';
  acceptLabel = 'Confirmar';
  rejectLabel = 'Cancelar';

  /**
   * @description Show cancel button
   */
  cancelButton!: boolean;

  /**
   * @description Show confirm action dialog
   * @param fx Alternative function
   * @param message Message of the dialog
   * @param header Header of the dialog
   */
  confirm(message: string, header: string, fx?: Function, cancelFx?: Function, icon?: string, options?: { acceptLabel?: string; rejectLabel?: string; silentCancel?: boolean }) {
    this.acceptLabel = options?.acceptLabel ?? 'Confirmar';
    this.rejectLabel = options?.rejectLabel ?? 'Cancelar';
    this.header = header;
    this.message = message;
    this.icon = icon ?? '';
    this.modalService.changeVisible(true);
    this.cancelButton = true;
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: this.icon || 'pi pi-exclamation-triangle',
      accept: () => {
        fx ? fx() : null;
      },
      reject: (type: ConfirmEventType) => {
        cancelFx ? cancelFx() : null;
        if (options?.silentCancel) return;
        switch (type) {
          case ConfirmEventType.REJECT:
            this.messageService.add({
              severity: 'error',
              summary: 'Rechazado',
              detail: 'Se ha rechazado la acción',
            });
            break;
          case ConfirmEventType.CANCEL:
            this.messageService.add({
              severity: 'warn',
              summary: 'Cancelado',
              detail: 'Se ha cancelado la acción',
            });
            break;
        }
      },
    });
  }

  // private component!: ComponentRef<any>;

  /**
   * @description Stop loading screen and send success message
   */
  stop(message: string, header: string, fx?: Function, error?: boolean, icon?: string) {
    this.header = header;
    this.message = message;
    this.icon = icon ?? '';
    this.modalService.changeVisible(true);
    setTimeout(() => {
      this.cancelButton = false;
      this.success(message, header, fx!, error);
    }, 300);
  }

  /**
   * @description Show success action dialog
   * @param fx Alternative function
   * @param message Message of the dialog
   * @param header Header of the dialog
   */
  success(message: string, header: string, fx?: Function, error?: boolean) {
    this.acceptLabel = 'Confirmar';
    this.rejectLabel = 'Cancelar';
    this.header = header;
    this.message = message;
    this.modalService.changeVisible(true);
    this.cancelButton = false;
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: this.icon || (!error ? 'pi pi-check' : 'pi pi-exclamation-triangle'),
      accept: () => {
        fx ? fx() : null;
      },
      reject: () => {
        fx ? fx() : null;
      },
    });
  }

  toast(
    message: string,
    summary: string,
    options?: { severity?: 'success' | 'info' | 'warn' | 'error'; life?: number; closable?: boolean; onClose?: () => void }
  ) {
    const life = options?.life ?? 3500;
    this.messageService.add({
      severity: options?.severity ?? 'info',
      summary,
      detail: message,
      life,
      closable: options?.closable ?? false,
    });
    if (options?.onClose) {
      setTimeout(() => options.onClose && options.onClose(), life);
    }
  }

  private readonly modalService = inject(ModalService);
  close() {
    this.message = '';
    this.header = '';
    this.modalService.changeVisible(false);
  }
}
